import type { ReactNode } from "react";
import { useRef, useLayoutEffect } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";
import { styled } from "styled-components";

import { ALERTS_SCROLL_CONTAINER_ID } from "../AlertsRootContainer";

const ESTIMATED_ITEM_HEIGHT = 180;
const ITEM_GAP = 16;
const OVERSCAN = 5;

const useScrollElement = () => {
    const scrollElementRef = useRef<HTMLElement | null>(null);

    useLayoutEffect(() => {
        scrollElementRef.current = document.getElementById(ALERTS_SCROLL_CONTAINER_ID);
    }, []);

    return () => scrollElementRef.current;
};

const VirtualList = styled.div`
    position: relative;
    width: 100%;
`;

const VirtualItem = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
`;

interface IVirtualizedProps {
    count: number;
    renderItem: (index: number) => ReactNode;
    getItemKey: (index: number) => string | number;
}

export const Virtualized = ({ count, renderItem, getItemKey }: IVirtualizedProps) => {
    const getScrollElement = useScrollElement();

    // oxlint-disable-next-line react/incompatible-library -- @tanstack/react-virtual's re-render model is incompatible with memoization; no compiler transform is enabled in this repo, so this is safe
    const virtualizer = useVirtualizer({
        count,
        getScrollElement,
        estimateSize: () => ESTIMATED_ITEM_HEIGHT,
        overscan: OVERSCAN,
        gap: ITEM_GAP,
        getItemKey,
    });

    const virtualItems = virtualizer.getVirtualItems();

    return (
        <VirtualList style={{ height: `${virtualizer.getTotalSize()}px` }}>
            {virtualItems.map((virtualRow) => (
                <VirtualItem
                    key={getItemKey(virtualRow.index)}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                    {renderItem(virtualRow.index)}
                </VirtualItem>
            ))}
        </VirtualList>
    );
};
